<script setup>
import {computed, ref} from "vue";
import generateId from "@/composables/generateId";
import IconChevronDown from "@/assets/icons/IconChevronDown.vue";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({"code": "+7", "phone": ""})
  },
  label: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: ""
  },
  alert: {
    type: String,
    default: ""
  },
  error: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: "medium",
    validator: (value) => ["small", "medium", "large"].includes(value)
  },
  phoneCodeList: {
    type: Array,
    default: () => []
  },
  autofocus: {
    type: Boolean,
    default: false
  }
});

const inputId = generateId();

const emit = defineEmits(["update:modelValue", "focus", "blur", "input", "change", "suffix-click"]);

const isFocused = ref(false);
const inputRef = ref(null);

const inputWrapperClasses = computed(() => [
  `input-wrapper--${props.size}`,
  {
    "input-wrapper--focused": isFocused.value || isShowCodeDropdown.value,
    "input-wrapper--disabled": props.disabled,
    "input-wrapper--error": props.error
  }
]);

const handleFocus = (event) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleBlur = (event) => {
  isFocused.value = false;
  emit("blur", event);
};

const localModelValue = computed(({
  set(value) {
    emit("update:modelValue", value);
  },
  get() {
    return props.modelValue;
  }
}));

const isShowCodeDropdown = ref(false);
const handleCodeDropdownClick = () => isShowCodeDropdown.value = !isShowCodeDropdown.value;

const setActivePhoneCode = (code) => {
  emit("update:modelValue", {...props.modelValue, code: code});
  isShowCodeDropdown.value = false;
};

const currentMask = computed(() => {
  const selectedCode = props.phoneCodeList.find(code => code.value === props.modelValue.code);
  return selectedCode ? selectedCode.mask : "(###) ###-##-##";
});

const formatPhoneNumber = (value, mask) => {
  const cleanValue = value.replace(/\D/g, "");
  let formatted = "";
  let valueIndex = 0;
  
  for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
    if (mask[i] === "#") {
      formatted += cleanValue[valueIndex];
      valueIndex++;
    } else {
      formatted += mask[i];
    }
  }
  
  return formatted;
};

const handlePhoneInput = (event) => {
  const inputValue = event.target.value;
  const formatted = formatPhoneNumber(inputValue, currentMask.value);
  
  emit("update:modelValue", { ...props.modelValue, phone: formatted });
  emit("input", event);
};

const focus = () => {
  if (inputRef.value) {
    inputRef.value.focus();
  }
};

defineExpose({
  focus
});
</script>

<template>
  <div class="input-container">
    <label
      v-if="props.label"
      :for="inputId"
      class="input-container__label"
    >
      {{ props.label }}
      <span
        v-if="required"
        class="input-container__label--required"
      >*</span>
    </label>
    <div
      :class="inputWrapperClasses"
      class="input-wrapper"
    >
      <div
        class="input-wrapper__code"
        :class="{'input-wrapper__code--active': isShowCodeDropdown}"
        @click="handleCodeDropdownClick"
      >
        {{ props.modelValue.code }}
        <IconChevronDown />
        <div
          v-if="isShowCodeDropdown"
          class="input-wrapper__code-dropdown"
        >
          <div
            v-for="code in phoneCodeList"
            :key="code.type"
            class="input-wrapper__code-item"
            @click.stop="setActivePhoneCode(code.value)"
          >
            <img
              class="input-wrapper__code-image"
              :src="require(`/public/icons/countries/${code.type}.png`)"
              alt=""
            >
            <span>
              {{ code.type }}
            </span>
            <span>
              {{ code.value }}
            </span>
          </div>
        </div>
      </div>
      <input
        ref="inputRef"
        v-model="localModelValue.phone"
        class="input-wrapper__input"
        :placeholder="props.placeholder || '(800) 555-35-35'"
        :disabled="props.disabled"
        :type="props.type"
        :autofocus="autofocus"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handlePhoneInput"
      >
    </div>
    <div
      v-if="props.alert"
      class="input-container__alert"
      :class="{'input-container__alert--error': props.error}"
    >
      {{ props.alert }}
    </div>
  </div>
</template>

<style scoped lang="scss">

.input-container {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__label {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: var(--form-primary-title-text);

    &--required { color: var(--system-red); }
  }

  &__alert {
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    color: var(--form-primary-placeholder-text);

    &--error {
      color: var(--system-red);
    }
  }
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 2px;
  background-color: var(--form-primary-default);
  border: 1px solid var(--form-primary-bd-default);
  padding: 0 12px;
  border-radius: 8px;
  color: var(--form-primary-title-text);

  &__code {
    position: relative;
    display: flex;
    align-items: center;
    gap: 2px;
    cursor: pointer;
    user-select: none;
    font-weight: 500;

    &-dropdown {
      position: absolute;
      z-index: 20;
      top: calc(100% + 15px);
      left: 10px;
      display: flex;
      flex-direction: column;
      width: 174px;
      padding: 6px;
      border-radius: 8px;
      background: #FFF;
      box-shadow: 1px 2px 10px 0 #131E2726;
      gap: 4px;
    }

    &-image {
      width: 21px;
      height: 14px;
    }

    &-item {
      box-sizing: border-box;
      font-size: 14px;
      font-weight: 400;
      display: flex;
      align-items: center;
      gap: 4px;
      height: 32px;
      padding: 6px 14px;
      border-radius: 8px;
      color: var(--text-text-main);

      &:hover {
        background: var(--background-bg-hover);
      }

      span:last-child {
        color: #A7A7A7;
        margin-left: auto;
      }
    }

    &--active {
      svg {
        transform: rotateX(180deg);
        color: var(--icon-icon-on);
      }
    }
  }

  &:hover:not(.input-wrapper--focused):not(.input-wrapper--disabled) {
    background-color: var(--form-primary-hover);
    border-color: var(--form-primary-bd-hover);
  }

  &--focused {
    background-color: var(--form-primary-typing);
    border-color: var(--form-primary-bd-typing);
  }

  &--error {
    border-color: var(--border-bd-error);
    background-color: var(--form-primary-default);
  }

  &--disabled {
    background-color: var(--form-primary-disabled);
    border-color: var(--border-bd-disabled);
    color: var(--text-text-disabled);
  }

  &__input {
    all: unset;
    width: 100%;
    height: 100%;
  }

  // Размеры
  &--small { height: 30px; }

  &--medium { height: 36px; }

  &--large { height: 42px; }

}

</style>